/**
 * Issue #3 — Projekt-Deeplink.
 *
 * Route /p/:ref nimmt eine menschenfreundliche Referenz (project_key wie
 * P021770 ODER eine Salesforce-Object-Id) und leitet auf die interne
 * Detail-Route /projects/:projectId um. Auflösung über GET /v1/projects/resolve/:ref
 * (Backend akzeptiert beide Identifier). Gedacht für Mail-/Backlog-/Chat-Links,
 * wo nur der fachliche Key bekannt ist, nicht die interne UUID.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';

export function ProjectDeeplinkPage() {
  const { t } = useTranslation();
  const { ref } = useParams<{ ref: string }>();
  const trimmed = (ref ?? '').trim();

  const query = useQuery({
    queryKey: ['project-resolve', trimmed],
    queryFn: () => api.resolveProjectRef(trimmed),
    enabled: trimmed.length > 0,
    retry: false,
    staleTime: 60_000,
  });

  if (trimmed.length > 0 && query.isLoading) {
    return (
      <div className="max-w-3xl mx-auto py-12">
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('v2.deeplink.resolving', 'Projekt „{{ref}}" wird aufgelöst…', { ref: trimmed })}
        </p>
      </div>
    );
  }

  if (query.data?.project_id) {
    return <Navigate to={`/projects/${query.data.project_id}`} replace />;
  }

  // Leere Referenz oder 404/Fehler → freundlicher Hinweis statt weißer Seite.
  return (
    <div className="max-w-3xl mx-auto py-12">
      <div
        className="text-[11px] uppercase tracking-widest mb-3"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {t('v2.deeplink.not_found_kicker', 'Deeplink')}
      </div>
      <h1
        className="text-2xl font-semibold tracking-tight mb-3"
        style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}
      >
        {t('v2.deeplink.not_found_title', 'Projekt nicht gefunden')}
      </h1>
      <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--ink-2)' }}>
        {t(
          'v2.deeplink.not_found_hint',
          'Zu „{{ref}}" konnte kein Projekt aufgelöst werden. Erwartet wird ein Projekt-Key (z. B. P021770) oder eine Salesforce-Id.',
          { ref: trimmed || '—' },
        )}
      </p>
      <Link to="/" className="text-sm" style={{ color: 'var(--primary)' }}>
        {t('v2.deeplink.back_home', '← Zur Übersicht')}
      </Link>
    </div>
  );
}
